import useSWR from "swr";
import { TrendingDown, TrendingUp } from "lucide-react";
import { CartesianGrid, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { fetcher } from "@/lib/api";
import { fmtNum } from "@/lib/format";
import { BRAND } from "@/lib/traffic";
import { cn } from "@/lib/utils";

function Tip({ active, payload }) {
  const d = payload?.[0]?.payload;
  if (!active || !d || d.accuracy_pct == null) return null;
  return (
    <div className="rounded-xl border border-border/70 bg-popover px-3 py-2 text-xs shadow-lg">
      <div className="font-semibold">{d.label} · {d.accuracy_pct}%</div>
      <div className="mt-1 text-muted-foreground">Predicted <span className="num text-foreground">{fmtNum(d.predicted)}</span> · Actual <span className="num text-foreground">{fmtNum(d.actual)}</span></div>
      <div className="text-muted-foreground">{d.hours} hourly values · {d.source === "live" ? "day-ahead prediction vs actual" : "hold-out test from the latest training"}</div>
    </div>
  );
}

// Day-by-day forecast accuracy over the last 30 days: solid = live day-ahead predictions scored against actual counts,
// dashed = hold-out test days from the latest training run (shown until live scores exist).
export function AccuracyTrend({ site, dir }) {
  const { data } = useSWR(`/traffic/forecast/accuracy?site=${site}&dir=${dir}&days=30`, fetcher, { refreshInterval: 300000 });
  const rows = (data?.days || []).map((d) => ({ ...d, live: d.source === "live" ? d.accuracy_pct : null, backtest: d.source === "backtest" ? d.accuracy_pct : null }));
  const scored = rows.filter((r) => r.accuracy_pct != null);
  const minY = scored.length ? Math.max(0, Math.floor((Math.min(...scored.map((r) => r.accuracy_pct)) - 10) / 10) * 10) : 0;
  const trend = data?.trend_pct;
  return (
    <section className="surface p-6" data-testid="forecast-accuracy-trend">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold md:text-lg">Day-ahead accuracy — last 30 days</h2>
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          {data?.mean_pct != null && <span data-testid="accuracy-mean">Average <span className="num font-semibold text-foreground">{data.mean_pct}%</span>{data.live_mean_pct != null ? <> · live <span className="num font-semibold text-foreground">{data.live_mean_pct}%</span> over {data.live_days} day{data.live_days === 1 ? "" : "s"}</> : null}</span>}
          {trend != null && (
            <span className={cn("num inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium", trend >= 0 ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300" : "bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300")} data-testid="accuracy-trend">
              {trend >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{trend >= 0 ? "+" : ""}{trend} pts vs previous week
            </span>
          )}
        </div>
      </div>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ left: -14, right: 28, top: 8 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={28} />
            <YAxis domain={[minY, 100]} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} />
            <Tooltip content={<Tip />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <ReferenceLine y={data?.target_pct ?? 85} stroke="#10b981" strokeDasharray="4 4" label={{ value: `target ${data?.target_pct ?? 85}%`, position: "insideTopRight", fontSize: 10, fill: "#10b981" }} />
            <Line type="monotone" dataKey="backtest" name="Hold-out test (training)" stroke={BRAND.blue} strokeWidth={1.5} strokeDasharray="5 4" dot={{ r: 2.5 }} connectNulls isAnimationActive={false} />
            <Line type="monotone" dataKey="live" name="Live: day-ahead vs actual" stroke={BRAND.red} strokeWidth={2.2} dot={{ r: 3 }} connectNulls isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground" data-testid="accuracy-note">
        {data && !scored.length ? "No scored days yet — the first live score appears the day after the model has been deployed."
          : `Accuracy = 100% − (sum of hourly prediction errors ÷ vehicles actually counted). Live points are scored each day from the prediction made the day before; dashed points come from the hold-out test of the latest training.${data?.live_days === 0 ? " Live points start accumulating from tomorrow." : ""}`}
      </p>
    </section>
  );
}
